import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import { IndicatorDetails, IndicatorDetailSectionProps } from '../utils';
import {
  fa1,
  fa3,
  fa4,
  fa7,
  faHyphen,
  faPlus,
  faFileChartColumn,
} from '@fortawesome/pro-light-svg-icons';
import { getMadLibForIndicator } from '../../ImpactIndicator/IndicatorMadLibs';
import { createDate, months } from 'shared/utils/formatUTC';
import TimePeriodButton from './TimePeriodButton';

export const getPeriodDuration = (
  s: Date | string | undefined,
  e: Date | string | undefined,
) => {
  const startDate = typeof s === 'string' ? createDate(s) : s;
  const endDate = typeof e === 'string' ? createDate(e) : e;

  let optionZeroYears: string | undefined = undefined;
  let optionThreeYears: string | undefined = undefined;
  let optionSeven: string | undefined = undefined;
  let optionMoreThanSeven: string | undefined = undefined;

  if (startDate && endDate) {
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const startMonth = startDate.getMonth();
    const endMonth = endDate.getMonth();
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    const startText = `${months[startMonth]} ${startDay}`;
    const endText = `${months[endMonth]} ${endDay}`;

    optionZeroYears = `${startText}, ${startYear} - ${endText}, ${endYear}`;
    optionThreeYears = `${startText}, ${startYear + 1} - ${endText}, ${endYear +
      3}`;
    optionSeven = `${startText}, ${startYear + 4} - ${endText}, ${endYear + 7}`;
    optionMoreThanSeven = `${startText}, ${startYear + 8}+`;
  }
  return {
    optionZeroYears,
    optionThreeYears,
    optionSeven,
    optionMoreThanSeven,
  };
};

const StyledDiv = styled.div`
  .program-heading {
    max-width: 630px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
  }
  .selection-buttons-container {
    display: inline-grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: ${SPACERS.medium};
    .selection-button {
      width: 196px;
      height: 188px;
      display: flex;
      cursor: pointer;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      border-radius: ${SPACERS.extraTiny};
      font-size: 16px;
      line-height: 21px;
      letter-spacing: -0.02em;
      border: 1px solid ${COLORS.ctaPrimary};
      transition: background 0.5s;
      padding: 0;
      background-color: ${COLORS.whiteBase};
      &:hover {
        background-color: ${COLORS.highlightBackground};
      }
      &.selected {
        color: ${COLORS.whiteBase};
        background-color: ${COLORS.ctaPrimary};
        .icon {
          color: ${COLORS.whiteBase};
        }
      }
      .icon {
        color: ${COLORS.ctaPrimary};
        margin-bottom: ${SPACERS.tiny};
      }
      .first.icon {
        margin-right: ${SPACERS.extraTiny};
      }
      .middle.icon {
        margin-right: ${SPACERS.extraTiny};
        margin-left: ${SPACERS.extraTiny};
      }
      .last.icon {
        margin-left: ${SPACERS.extraTiny};
      }
      .text {
        margin-top: 2px;
      }
      .small-text {
        font-size: 13px;
        line-height: 15.6px;
        letter-spacing: -0.02em;
        margin-top: 10px;
      }
      .button-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
      }
      .date-time-period {
        width: 100%;
        margin-top: 0;
        color: ${COLORS.blackBase};
        padding-top: ${SPACERS.small};
        padding-bottom: ${SPACERS.small};
        background-color: ${COLORS.grayLight};
        border-bottom-left-radius: ${SPACERS.extraTiny};
        border-bottom-right-radius: ${SPACERS.extraTiny};
      }
    }
    @media screen and (max-width: 1210px) {
      grid-template-columns: 1fr 1fr;
    }
    @media screen and (max-width: 1010px) {
      grid-template-columns: 1fr;
      .selection-button {
        justify-self: center;
      }
    }
  }
  .date-section-text {
    margin-top: ${SPACERS.large};
    .date-section-link {
      display: inline;
      cursor: pointer;
      color: ${COLORS.ctaPrimary};
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const IndicatorTimePeriod: React.FC<IndicatorDetailSectionProps & {
  indicatorDetails: IndicatorDetails;
  goToDateSection?: () => unknown;
}> = ({
  indicator,
  requestState,
  indicatorDetails,
  selectedIndex,
  selectedSectionKey,
  beneficiaryTerm,
  reportPeriod,
  goToDateSection,
  setIndicators,
  onNext,
  onPrevious,
  handleIndicatorSave,
}) => {
  const [timePeriod, setTimePeriod] = useState<IndicatorDetails['timePeriod']>(
    indicatorDetails.timePeriod,
  );

  useEffect(() => {
    setTimePeriod(indicatorDetails.timePeriod);
  }, [indicatorDetails]);

  const handleNext = useCallback(
    (newTimePeriod?: IndicatorDetails['timePeriod'], isButton?: boolean) => {
      const value = newTimePeriod === undefined ? timePeriod : newTimePeriod;
      if (isButton) {
        onNext({ timePeriod: value });
      }
      setIndicators(s => {
        const newState = { ...s };
        newState[selectedSectionKey][selectedIndex].timePeriod = value;
        return newState;
      });
      const newIndicatorDetails = { ...indicatorDetails };
      newIndicatorDetails.timePeriod = value;
      handleIndicatorSave(newIndicatorDetails);
    },
    [
      timePeriod,
      selectedIndex,
      indicatorDetails,
      selectedSectionKey,
      onNext,
      setIndicators,
      handleIndicatorSave,
    ],
  );

  const handleClick = (newTimePeriod: IndicatorDetails['timePeriod']) => {
    setTimePeriod(newTimePeriod);
    if (timePeriod === undefined) {
      handleNext(newTimePeriod);
    }
  };

  const madLibIndicator = getMadLibForIndicator(indicator);

  const {
    optionZeroYears,
    optionThreeYears,
    optionSeven,
    optionMoreThanSeven,
  } = getPeriodDuration(reportPeriod.startDate, reportPeriod.endDate);

  const lowerCaseBeneficiaryTerm = beneficiaryTerm?.toLowerCase();
  const unit =
    madLibIndicator?.unit === 'people' && beneficiaryTerm
      ? lowerCaseBeneficiaryTerm === 'animals'
        ? 'people'
        : lowerCaseBeneficiaryTerm
      : madLibIndicator?.unit;

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-page" className="program-heading">
        How long will it take for {unit || 'people'} to{' '}
        {madLibIndicator?.outcome || 'achieve goals'} as a result of this
        program?
      </TrueImpactText>
      <div className="selection-buttons-container">
        <TimePeriodButton
          selected={timePeriod === 'Immediate'}
          onClick={() => handleClick('Immediate')}
          firstIcon={undefined}
          middleIcon={faFileChartColumn}
          lastIcon={undefined}
          firstLine={undefined}
          secondLine="During your report period"
          timePeriod={optionZeroYears}
        />
        <TimePeriodButton
          selected={timePeriod === 'Short term'}
          onClick={() => handleClick('Short term')}
          firstIcon={fa1}
          middleIcon={faHyphen}
          lastIcon={fa3}
          firstLine="Years"
          secondLine="(Short term)"
          timePeriod={optionThreeYears}
        />
        <TimePeriodButton
          selected={timePeriod === 'Medium term'}
          onClick={() => handleClick('Medium term')}
          firstIcon={fa4}
          middleIcon={faHyphen}
          lastIcon={fa7}
          firstLine="Years"
          secondLine="(Medium term)"
          timePeriod={optionSeven}
        />
        <TimePeriodButton
          selected={timePeriod === 'Long term'}
          onClick={() => handleClick('Long term')}
          firstIcon={fa7}
          middleIcon={undefined}
          lastIcon={faPlus}
          firstLine="Years"
          secondLine="(Long term)"
          timePeriod={optionMoreThanSeven}
        />
      </div>
      {goToDateSection &&
        !optionThreeYears &&
        !optionSeven &&
        !optionMoreThanSeven && (
          <TrueImpactText className="date-section-text">
            Not sure what dates these would be?{' '}
            <div className="date-section-link" onClick={goToDateSection}>
              Complete the Dates section of your report now
            </div>
          </TrueImpactText>
        )}
      <IndicatorBottomButtons
        requestState={requestState}
        disabledNext={!timePeriod}
        onNext={() => handleNext(undefined, true)}
        onPrevious={onPrevious}
      />
    </StyledDiv>
  );
};

export default IndicatorTimePeriod;
